import React from 'react';
import PropTypes from 'prop-types';
import { AppBar, Tab, Tabs, Box, Container } from '@material-ui/core';
import { Link, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useAnalytics } from 'use-analytics';

import TabPanel from './tab-panel';
import { useAppState } from '../app-context/src';
import { makeStyles } from '@material-ui/core/styles';

const a11yProps = (index) => {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
};

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.common.white,
  },
  indicator: {
    backgroundColor: theme.palette.primary.light,
  },
  selected: {
    color: theme.palette.primary.light,
    fontWeight: theme.typography.fontWeightBold,
  },
}));

const TabContainer = (props) => {
  const { children } = props;
  const appState = useAppState();
  const analytics = useAnalytics();
  const { paramTabName } = useParams();
  const intl = useIntl();
  const classes = useStyles();
  const tabsActions = React.useRef();

  // Find out active tab from URL param
  const getActiveTabIndex = (name) =>
    React.Children.toArray(children).reduce((acc, child, i) => {
      if (child.props.route === name) {
        return i;
      }
      return acc;
    }, 0);

  // Get route by tab index
  const getTabRoute = (indx) => {
    const child = React.Children.toArray(children)[indx];
    return child.props.route;
  };

  const [tabIndex, setTabIndex] = React.useState(
    getActiveTabIndex(paramTabName)
  );

  React.useEffect(() => {
    analytics.track('b view tab', {
      category: 'browse',
      bookshelf: appState.shelfCode,
      label: paramTabName,
    });
    const activeIndex = React.Children.toArray(children).reduce(
      (acc, child, i) => {
        if (child.props.route === paramTabName) {
          return i;
        }
        return acc;
      },
      0
    );
    setTabIndex(activeIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramTabName]);

  // Render the tabs meny that switches between tab panels
  const renderTabMenu = (children) => {
    return React.Children.map(children, (child, indx) => {
      return (
        <Tab
          key={`tab-${child.props.route}`}
          label={intl.formatMessage({
            id: child.props.label,
          })}
          {...a11yProps(indx)}
          component={'h2'}
          to={child.props.route}
          classes={{ selected: classes.selected }}
        />
      );
    });
  };

  // Render tab panels containing the actual content
  const renderTabPanels = (children) => {
    return React.Children.map(children, (child, indx) => {
      return (
        <TabPanel index={indx} value={tabIndex}>
          {child}
        </TabPanel>
      );
    });
  };

  return (
    <Box position="static">
      {/* Tab Bar */}
      <AppBar position="relative" color="common.white">
        <Container disableGutters={true} maxWidth="lg">
          <Tabs
            action={tabsActions}
            value={tabIndex}
            onChange={(event, tabIndex) => {
              const route = getTabRoute(tabIndex);
              analytics.track('b switch tab', {
                category: 'browse',
                bookshelf: appState.shelfCode,
                label: route,
              });
              setTabIndex(tabIndex);
            }}
            classes={{
              indicator: classes.indicator,
            }}
            textColor="inherit"
            variant="fullWidth"
            aria-label={intl.formatMessage({ id: 'nav.aria.shelf_tabs' })}
            dir={appState.direction}
          >
            {renderTabMenu(children)}
          </Tabs>
        </Container>
      </AppBar>

      {/* Tab Content */}
      {renderTabPanels(children)}
    </Box>
  );
};

TabContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

export default TabContainer;
